/**
 * @typedef {Object} GridPresetStatePayload
 * @property {import("../dataGridUrlState/dataGridUrlStateCodec.js").GridFilterItem[]} filterItems
 * @property {string} quickFilterValue
 * @property {import("../dataGridUrlState/dataGridUrlStateCodec.js").GridSortItem[]} sortModel
 * @property {number} pageSize
 * @property {Record<string, boolean>} columnVisibilityModel
 */

/**
 * @param {string} field
 * @param {Set<string>|Array<string>|undefined} allowedFields
 * @returns {boolean}
 */
function isAllowedField(field, allowedFields) {
  if (!allowedFields) {
    return true;
  }
  const set = allowedFields instanceof Set ? allowedFields : new Set(allowedFields);
  return set.has(field);
}

/**
 * Extracts persistable preset state from current grid models (excludes page index).
 *
 * @param {Object} state
 * @param {import("@mui/x-data-grid").GridFilterModel} state.filterModel
 * @param {import("@mui/x-data-grid").GridSortModel} state.sortModel
 * @param {import("@mui/x-data-grid").GridPaginationModel} state.paginationModel
 * @param {import("@mui/x-data-grid").GridColumnVisibilityModel} state.columnVisibilityModel
 * @param {Object} [options]
 * @param {string[]} [options.allowedFields]
 * @returns {GridPresetStatePayload}
 */
export function extractPresetStateFromGrid(
  { filterModel, sortModel, paginationModel, columnVisibilityModel },
  options = {},
) {
  const allowedSet = options.allowedFields ? new Set(options.allowedFields) : undefined;

  const filterItems = (filterModel?.items ?? []).filter(
    (item) => item?.field && isAllowedField(item.field, allowedSet),
  );

  return {
    filterItems,
    quickFilterValue: filterModel?.quickFilterValues?.[0] ?? "",
    sortModel: (sortModel ?? []).filter((item) => item?.field && isAllowedField(item.field, allowedSet)),
    pageSize: paginationModel?.pageSize ?? 25,
    columnVisibilityModel: Object.fromEntries(
      Object.entries(columnVisibilityModel ?? {}).filter(([field]) => isAllowedField(field, allowedSet)),
    ),
  };
}

/**
 * Converts stored preset payload into grid-controlled models (page always 0).
 *
 * @param {GridPresetStatePayload} presetState
 * @param {number} defaultPageSize
 * @param {Object} [options]
 * @param {string[]} [options.allowedFields]
 * @returns {{
 *   filterModel: import("@mui/x-data-grid").GridFilterModel,
 *   sortModel: import("@mui/x-data-grid").GridSortModel,
 *   paginationModel: import("@mui/x-data-grid").GridPaginationModel,
 *   columnVisibilityModel: import("@mui/x-data-grid").GridColumnVisibilityModel,
 * }}
 */
export function applyPresetStateToGrid(presetState, defaultPageSize, options = {}) {
  const allowedSet = options.allowedFields ? new Set(options.allowedFields) : undefined;
  const safeState = presetState ?? {};

  const filterItems = (safeState.filterItems ?? []).filter(
    (item) => item?.field && isAllowedField(item.field, allowedSet),
  );
  const quickFilterValue = safeState.quickFilterValue ?? "";

  return {
    filterModel: {
      items: filterItems,
      quickFilterValues: quickFilterValue ? [quickFilterValue] : [],
    },
    sortModel: (safeState.sortModel ?? []).filter((item) => item?.field && isAllowedField(item.field, allowedSet)),
    paginationModel: {
      page: 0,
      pageSize: safeState.pageSize ?? defaultPageSize,
    },
    columnVisibilityModel: Object.fromEntries(
      Object.entries(safeState.columnVisibilityModel ?? {}).filter(([field]) => isAllowedField(field, allowedSet)),
    ),
  };
}
